/*
 * Copyright (c) 2021 biocrates life sciences ag, All rights reserved.
 */

import {StringKeyOf} from "ts-enum-util/dist/types/types"
import {ActionType, createAction} from "typesafe-actions"

import {ColumnWidth} from "../../core/models/columnWidth"
import {TableFiltering} from "../../core/models/tableFiltering"
import {TableSorting} from "../../core/models/tableSorting"
import GroupSettings from "../models/groupSettings"
import {ModuleSettingsActions} from "../models/moduleSettingsActions"
import Setting, {SettingValue} from "../models/setting"
import SettingMetadata from "../models/settingMetadata"

export const updateSettingAction = createAction("settings/UPDATE_SETTING")<
    Setting<any, any, any>
>()
export const updateSettingSuccessAction = createAction(
    "settings/UPDATE_SETTING_SUCCESS"
)<void>()

export const getSettingAction = createAction("settings/GET_SETTING")<
    SettingMetadata<any, any>
>()
export const getSettingSuccessAction = createAction(
    "settings/GET_SETTING_SUCCESS"
)<Setting<any, any, any>>()

export const getGroupSettingsAction = createAction(
    "settings/GET_GROUP_SETTINGS"
)<string>()
export const getGroupSettingsSuccessAction = createAction(
    "settings/GET_GROUP_SETTINGS_SUCCESS"
)<GroupSettings<any, any>>()

// Actions to be used in application

export const getModuleSpecificActions = <S, G>(
    module: StringKeyOf<S>
): ModuleSettingsActions<S, G> => ({
    module,
    updateSetting: <V extends SettingValue>(key: StringKeyOf<G>, value: V) =>
        updateSettingAction({group: module, key, value}),
    getSetting: (key: StringKeyOf<G>) => getSettingAction({group: module, key}),
    getAllSettings: () => getGroupSettingsAction(module),

    updateTableFiltering: <V>(
        key: StringKeyOf<G>,
        value: TableFiltering<V>[]
    ) => updateSettingAction({group: module, key, value}),
    updateTableSorting: (key: StringKeyOf<G>, value: TableSorting[]) =>
        updateSettingAction({group: module, key, value}),
    updateColumnWidths: (key: StringKeyOf<G>, value: ColumnWidth[]) =>
        updateSettingAction({group: module, key, value}),
})

export type SettingsActions = ActionType<
    | typeof updateSettingAction
    | typeof getSettingAction
    | typeof getSettingSuccessAction
    | typeof getGroupSettingsAction
    | typeof getGroupSettingsSuccessAction
>
